import styled from '@emotion/styled';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import ExternalLink from 'src/components/ExternalLink';
import { ModalContent as ModalContentBase } from 'src/components/ModalLayout';
import EmptyProducts from 'src/components/Shop/EmptyProducts';
import ShopLoading from 'src/components/Shop/Loading';
import ProductLoadingFailure from 'src/components/Shop/ProductLoadingFailure';
import ShopPromotionBanner from 'src/components/Shop/PromotionBanner';
import { useGetStarterPackagePopupQuery } from 'src/hooks/useContentsMatchQuery';
import { useGetGemProductsQuery } from 'src/hooks/useGemProductQuery';
import { useGetTermsUrl } from 'src/hooks/useGetTermsUrl';
import useParseStarterPackagePopup from 'src/hooks/useParseStarterPackagePopup';
import { useShopPageQueryOptions } from 'src/hooks/useShopQuery';
import useTermsInfo from 'src/hooks/useTermsInfo';
import { PgPaymentMethod } from 'src/types/Payments';
import ShopContentGemProducts from './GemProducts';
const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`;
const GemProducts = styled(ShopContentGemProducts)`
  width: 100%;
`;
const DescWrapper = styled.ul`
  margin: 0;
  padding: 18px 0 0 24px;
`;
const LegalDesc = styled.li`
  font-size: 13px;
  color: ${({
  theme
}) => theme.color.content7};
  margin: 0px;
  padding: 0px;
  & + & {
    margin-top: 10px;
  }
`;
const LegalLink = styled(ExternalLink)`
  color: inherit;
`;
const ModalContent = styled(ModalContentBase)`
  padding-top: 16px;
  padding-bottom: 24px;
`;
const ShopContent = () => {
  const {
    t
  } = useTranslation();

  // 상점 열 때마다 상품 목록 새롭게 획득
  const {
    isLoading: isLoadingGemProducts,
    data: gemProductsData,
    isError: isGemProductsQueryFailed
  } = useGetGemProductsQuery({
    refetchOnMount: 'always'
  });
  const {
    data: {
      pgPaymentMethod,
      shop
    }
  } = useSuspenseQuery(useShopPageQueryOptions());
  const {
    gemProducts,
    bonusGemRates
  } = gemProductsData?.data || {};
  const {
    isGDPR
  } = useTermsInfo();
  const {
    termOfUse
  } = useGetTermsUrl();
  const {
    isLoading: isLoadingStarterPackagePopup,
    data
  } = useGetStarterPackagePopupQuery({
    refetchOnMount: true
  });
  const {
    starterPackageModule,
    singleProductModule
  } = useParseStarterPackagePopup(data?.data.result.popup);
  if (isGemProductsQueryFailed) {
    return <ProductLoadingFailure />;
  }
  if (isLoadingGemProducts || isLoadingStarterPackagePopup || gemProducts === undefined) {
    return <ShopLoading />;
  }
  if (!gemProducts?.length) {
    return <EmptyProducts />;
  }
  return <ModalContent data-sentry-element="ModalContent" data-sentry-component="ShopContent" data-sentry-source-file="index.tsx">
      <ShopPromotionBanner gemProducts={gemProducts} bonusGemRates={bonusGemRates} data-sentry-element="ShopPromotionBanner" data-sentry-source-file="index.tsx" />
      <ProductsContainer data-sentry-element="ProductsContainer" data-sentry-source-file="index.tsx">
        <GemProducts page={shop} gemProducts={gemProducts} starterPackageModule={starterPackageModule} singleProductModule={singleProductModule} data-sentry-element="GemProducts" data-sentry-source-file="index.tsx" />
      </ProductsContainer>
      <DescWrapper data-sentry-element="DescWrapper" data-sentry-source-file="index.tsx">
        <LegalDesc data-sentry-element="LegalDesc" data-sentry-source-file="index.tsx">{t('shop_bottom_notice')}</LegalDesc>
        {pgPaymentMethod === PgPaymentMethod.TOSS && <>
            <LegalDesc>{t('shop_refund_explanation_kr')}</LegalDesc>
            <LegalDesc>
              <LegalLink href='https://azarlive.com/home/ko-KR/refundPolicy.html'>
                {t('shop_refund_minor_explanation_kr')}
              </LegalLink>
            </LegalDesc>
          </>}
        {isGDPR && <>
            <LegalDesc>{t('shop_vat_explanation_eu')}</LegalDesc>
            <LegalDesc>{t('shop_refund_explanation_eu')}</LegalDesc>
            <LegalDesc>
              <LegalLink href={termOfUse}>{t('shop_more_information_eu')}</LegalLink>
            </LegalDesc>
          </>}
      </DescWrapper>
    </ModalContent>;
};
export default ShopContent;